import { Divider, Drawer, Stack, useTheme } from "@mui/material";
import { Button, FoodsIconButton, LinearProgress, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type NetworkQuestionnaireProgress, useGetNetworkMembers } from "src/api/client-api/network/useNetworkLists";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { useUserNew } from "src/api/client-api/useUserNew";
import { useFoodpilotContext, ORGANIZATION, VIEW_SSQ } from "src/context/FoodpilotContext";
import {
    buildOrganizationPermissions,
    getSelectableCompanies,
    hasScopedOrganizationPermission,
} from "src/context/permissionMatching";
import { CompaniesService } from "src/services/Companies/CompaniesService";
import { URLHandler } from "src/services/URLHandler";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { NetworkStatusTag } from "./useNetworkOrganizationColumns";
import { networkMemberName } from "./useNetworkMemberColumns";
import { NetworkError } from "./networkListParts";
import { useShortDateFormatter } from "./useShortDateFormatter";
import { useNetworkAnswersLabels } from "./useNetworkAnswersLabels";

const PANEL_WIDTH = 66;
const PANEL_TITLE_ID = "network-organization-panel-title";
const MEMBERS_PAGE_SIZE = 200;

type NetworkOrganizationPanelProps = {
    row: NetworkQuestionnaireProgress | null;
    onClose: () => void;
};

export const NetworkOrganizationPanel = (props: NetworkOrganizationPanelProps) => {
    const { row, onClose } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { isMobile } = useResponsiveBreakpoints();

    return (
        <Drawer
            anchor="right"
            open={row !== null}
            onClose={onClose}
            PaperProps={{
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": PANEL_TITLE_ID,
                sx: { width: isMobile ? "100%" : theme.spacing(PANEL_WIDTH), maxWidth: "100%" },
            }}
        >
            {row !== null && (
                <Stack padding={theme.spacing(4, 4)} gap={theme.spacing(3)}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography id={PANEL_TITLE_ID} variant="h3">
                                {row.organizationName}
                            </Typography>
                            <PanelCampaign />
                        </Stack>
                        <FoodsIconButton
                            icon="close"
                            size="s"
                            tooltip={{ text: t("common.close", "Close") }}
                            buttonProps={{ onClick: onClose }}
                        />
                    </Stack>
                    <Divider />
                    <QuestionnaireProgress row={row} />
                    <Divider />
                    <OrganizationMembers organizationId={row.organizationId} />
                </Stack>
            )}
        </Drawer>
    );
};

const PanelCampaign = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { currentCampaign } = useFoodpilotContext();

    return (
        <Typography variant="body" sx={{ color: theme.custom.grey[2200] }}>
            {t("network.panel.campaign", "Campaign {{label}}", { label: currentCampaign?.label ?? "" })}
        </Typography>
    );
};

const QuestionnaireProgress = (props: { row: NetworkQuestionnaireProgress }) => {
    const { row } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const formatDate = useShortDateFormatter();
    const { answersLabel } = useNetworkAnswersLabels();

    return (
        <Stack gap={theme.spacing(1.5)}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={theme.spacing(2)}>
                <Typography variant="body-bold">{row.questionnaireTitle ?? "-"}</Typography>
                <NetworkStatusTag status={row.status} />
            </Stack>
            <LinearProgress value={row.completionRate} />
            <Typography variant="small" sx={{ color: theme.custom.grey[2200] }}>
                {answersLabel(row)}
            </Typography>
            {row.lastChangedAt && (
                <Typography variant="small" sx={{ color: theme.custom.grey[2200] }}>
                    {t("network.panel.last_update", "Last update {{date}}", { date: formatDate(row.lastChangedAt) })}
                </Typography>
            )}
            {row.lastContributorEmail && (
                <Typography variant="small" sx={{ color: theme.custom.grey[2200] }}>
                    {t("network.panel.last_contributor", "Last contributor {{email}}", {
                        email: row.lastContributorEmail,
                    })}
                </Typography>
            )}
            <OpenQuestionnaireLink row={row} />
        </Stack>
    );
};

const OpenQuestionnaireLink = (props: { row: NetworkQuestionnaireProgress }) => {
    const { row } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useUserNew();
    const { currentCampaign, setSelectedCompany } = useFoodpilotContext();
    const organization =
        user === undefined ? undefined : (
            getSelectableCompanies(user).find((candidate) => candidate.id === row.organizationId)
        );
    const canOpen =
        user !== undefined &&
        organization !== undefined &&
        (user.roles.includes("ROLE_ADMIN") ||
            hasScopedOrganizationPermission(
                buildOrganizationPermissions(user.organizations),
                row.organizationId,
                VIEW_SSQ,
                ORGANIZATION,
            ));
    const { ssq } = useGetOneSsq(canOpen ? row.ssqId : undefined);
    const firstScoreId = ssq?.scores[0]?.id;

    if (!canOpen || organization === undefined || currentCampaign === null || firstScoreId === undefined) {
        return null;
    }

    const openQuestionnaire = () => {
        CompaniesService.switchCompany(organization.id);
        setSelectedCompany(organization);
        navigate(
            URLHandler.company.questionnaireScore(organization.id, row.ssqId, currentCampaign.id, String(firstScoreId)),
        );
    };

    return (
        <Button variant="text" onClick={openQuestionnaire} sx={{ alignSelf: "flex-start", paddingX: 0 }}>
            {t("network.panel.open_questionnaire", "Open the questionnaire")}
        </Button>
    );
};

const OrganizationMembers = (props: { organizationId: number }) => {
    const { organizationId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const formatDate = useShortDateFormatter();
    const { selectedCompany } = useFoodpilotContext();
    const { rows, totalItems, isReady, errorStatus, refetch } = useGetNetworkMembers({
        organizationId: selectedCompany?.id,
        params: { organization: [String(organizationId)], itemsPerPage: MEMBERS_PAGE_SIZE },
    });

    if (errorStatus !== null) {
        return <NetworkError status={errorStatus} onRetry={refetch} />;
    }

    return (
        <Stack gap={theme.spacing(1.5)}>
            <Typography variant="body-bold">
                {isReady ?
                    t("network.panel.members", "Members · {{total}}", { total: totalItems })
                :   t("network.panel.members_loading", "Members")}
            </Typography>
            <Stack divider={<Divider />}>
                {rows.map((member) => (
                    <Stack
                        key={member.id}
                        direction="row"
                        justifyContent="space-between"
                        gap={theme.spacing(2)}
                        paddingY={theme.spacing(1)}
                    >
                        <Stack minWidth={0}>
                            <Typography variant="body-bold">{networkMemberName(member) || member.email}</Typography>
                            <Typography variant="small" sx={{ color: theme.custom.grey[2200] }}>
                                {member.email}
                            </Typography>
                        </Stack>
                        <Stack alignItems="flex-end" flexShrink={0}>
                            <Typography variant="small">
                                {member.lastLoginAt ?
                                    t("network.panel.last_login", "Last login {{date}}", {
                                        date: formatDate(member.lastLoginAt),
                                    })
                                :   t("network.panel.never_logged_in", "Never logged in")}
                            </Typography>
                            <Typography variant="small" sx={{ color: theme.custom.grey[2200] }}>
                                {member.termsAcceptedAt ?
                                    t("network.panel.terms_accepted", "Terms accepted {{date}}", {
                                        date: formatDate(member.termsAcceptedAt),
                                    })
                                :   t("network.panel.terms_not_accepted", "Terms not accepted")}
                            </Typography>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};
